'use es6';

import promiseDone from 'hs-promise-utils/promiseDone';
/*
 * wrapFluxAction, wrapReduxAction (have same method signature)
 * Convenience and standardization methods for actions in flux and redux.
 * Dispatches *_START/*_SUCCEEDED/*_FAILED actions for an actionStem and ensures
 * proper payloads are passed along. it will `.done()` the promise chain to make sure
 * errors are bubbled up.
 *
 * Args:
 * ---
 * apiCall: The Api method you want to call. Use `myFxn.bind(myFxn, args...)` if you need to pass args
 * actionStem: Base stem of action type string. Eg for 'SOME_FETCH_SUCCEEDED', the actionStem is 'SOME_FETCH'
 * options:
 * successParams: object of params that will be spread into success payload along with response
 * startParams: will be payload of start dispatch. can be any type
 * failParams: object of params that will be spread into payload for fail
 * allParams: overrides start/fail/successParams
 * returnPromise: [true/false], set to true if you want the promise returned (for example if you want to Q.all)
 * success: custom success handler
 * start: custom start handler
 * fail: custom fail handler
 *
 *
 */
// This is necessary because this function deliberately does not return unless returnPromise
// is true.
// eslint-disable-next-line consistent-return

const wrapAction = function wrapAction(apiCall, actionStem, opts) {
  let {
    start,
    success,
    fail,
    successParams,
    failParams,
    startParams
  } = opts;
  const {
    dispatch,
    typeKey,
    payloadKey,
    returnPromise,
    allParams,
    transform,
    filter
  } = opts;

  if (!dispatch) {
    throw new Error('Missing dispatch function in action wrapper');
  }

  if (allParams) {
    startParams = allParams;
    successParams = allParams;
    failParams = allParams;
  }

  if (!start) {
    start = function () {
      dispatch({
        [typeKey]: `${actionStem}_START`,
        [payloadKey]: startParams
      });
    };
  }

  if (!success) {
    success = response => {
      // response could be an array
      const filteredResponse = filter ? filter(response) : response;
      response = transform ? transform(filteredResponse) : filteredResponse;
      const payload = successParams ? Object.assign({}, successParams, response) : response;
      dispatch({
        [typeKey]: `${actionStem}_SUCCEEDED`,
        [payloadKey]: payload
      });
      return payload;
    };
  }

  if (!fail) {
    fail = response => {
      let responseJSON;

      try {
        responseJSON = response.data && JSON.parse(response.data);
      } catch (err) {
        responseJSON = null;
      }

      const failedMetaObject = {
        message: response.message,
        status: response.status,
        statusText: response.statusText,
        responseJSON
      };
      dispatch({
        [typeKey]: `${actionStem}_FAILED`,
        [payloadKey]: Object.assign({}, failParams),
        meta: Object.assign({}, failedMetaObject)
      }); // Make sure if chaining promises the subsequent fail handler is called

      if (returnPromise) {
        throw new Error(`ActionWrapper failed request: ${actionStem}_FAILED`, {
          cause: responseJSON
        });
      }
    };
  }

  start();
  const promise = apiCall().then(success, fail);

  if (returnPromise) {
    return promise;
  } else {
    // make sure to chain with done so errors are tracked by Sentry/Raven
    promiseDone(promise);
  }
};

export const wrapReduxAction = function wrapReduxAction(apiCall, actionStem, opts = {}) {
  return wrapAction(apiCall, actionStem, Object.assign({}, opts, {
    typeKey: 'type',
    payloadKey: 'payload'
  }));
};